import { useCallback } from 'react';
import { MIXPASS_SALE_CONFIG } from 'components/Mixpass/mixpassConfig';
import useMixpassSaleStatus from './useMixpassSaleStatus';
import useMixpassWhitelistStatus from './useMixpassWhitelistStatus';
import useMixpassWhitelistUserStatus from './useMixpassWhitelistUserStatus';
import { Contracts, useContract } from './useContract';
import { calculateGasMargin } from 'utils/transactions';
import useTransactionHandler from './useTransactionHandler';
import { useWallet } from 'components/WalletContext';
import { futuraChainId } from 'utils/config';
import { getAllocation } from 'utils/whitelist';
import { useQueryClient } from 'react-query';

const useMixpassSale = (whitelist: string) => {
  const { network, address } = useWallet();
  const queryClient = useQueryClient();
  const contract = useContract(
    MIXPASS_SALE_CONFIG.contractAddress,
    Contracts.MixpassSale,
    futuraChainId
  );
  const { txHandler } = useTransactionHandler(futuraChainId);

  const {
    price,
    remainingSupply,
    paymentToken,
    isLoading: saleStatusLoading,
    isError: saleStatusError,
  } = useMixpassSaleStatus();

  const {
    isEnabled,
    isLoading: whitelistStatusLoading,
    isError: whitelistStatusError,
  } = useMixpassWhitelistStatus(whitelist);

  const {
    claimStatus,
    userClaimInfo,
    merkleRoot,
    isLoading: claimStatusLoading,
    isError: claimStatusError,
  } = useMixpassWhitelistUserStatus(whitelist);

  const buy = useCallback(
    async (amount: number, cb?: any) => {
      console.log('Buying', amount);

      if (userClaimInfo && price && Boolean(contract)) {
        try {
          const allocation = getAllocation(userClaimInfo);

          if (allocation <= 0) throw new Error('Allocation must be greater than 0');
          if (userClaimInfo.proof.length <= 0) throw new Error('Proof not valid');
          if (userClaimInfo.index < 0) throw new Error('Leaf index not valid');

          const whitelistId = whitelist.valueOf();
          const merkleProof = userClaimInfo.proof;
          const leafIndex = userClaimInfo.index;

          if (!amount) throw new Error('Amount must be greater than 0');
          if (!allocation) throw new Error('No allocation for user');
          if (network !== futuraChainId) throw new Error('Wrong network');

          const onChainMerkleRoot = await contract!.getWhitelistMerkleRoot(whitelist);
          if (merkleRoot !== onChainMerkleRoot)
            throw new Error('Configuration error, merkle roots do not match');

          // @ts-ignore
          const estimateGas = await contract!.estimateGas.buyWhitelist(
            whitelistId,
            amount,
            leafIndex,
            allocation,
            merkleProof
          );

          console.log('Claim estimate gas:', estimateGas);

          const res = await contract!.buyWhitelist(
            whitelistId,
            amount,
            leafIndex,
            allocation,
            merkleProof,
            {
              gasLimit: calculateGasMargin(estimateGas, 200000),
            }
          );
          txHandler(res, (link, ...tx) => {
            queryClient.setQueryData(
              ['mixpass-whitelist-claim-status', whitelist, address, leafIndex],
              true
            );
          });
        } catch (e) {
          console.debug('Failed to claim', e);
          throw e;
        }
      }
    },
    [
      userClaimInfo,
      whitelist,
      price,
      contract,
      network,
      txHandler,
      address,
      queryClient,
      merkleRoot,
    ]
  );

  return {
    isEnabled,
    userClaimInfo,
    claimStatus,
    isLoading: whitelistStatusLoading || saleStatusLoading || claimStatusLoading,
    isError: whitelistStatusError || saleStatusError || claimStatusError,
    buy,
    price,
    remainingSupply,
    paymentToken,
  };
};

export default useMixpassSale;
